import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { Typography, Select, MenuItem, Button, IconButton, Slide, useScrollTrigger } from "@mui/material";
import { ChevronDown } from "lucide-react";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../../ui/dropdown-menu";
import { Button as ShadcnButton } from "../../../../ui/button";
//Actions
import { setWalkthroughSection, showMacrosModal, hideMacrosModal } from "../../../../../actions/walkthrough";

import { Link } from "react-router-dom";

import MacrosModal from "../modals/MacrosModal";
import CustomModal from "../../../../modal/CustomModal";
import PlusModal from "../../../../plus/PlusModal";
const WalkthroughNav = ({
  walkthrough: { recipe, user, currentSection, modals },
  plus: { isPlus },
  setWalkthroughSection,
  handleFinishRecipe,
  setNavHeight,
  showMacrosModal,
  hideMacrosModal,
}) => {
  const trigger = useScrollTrigger();

  const navRef = useRef(null);

  const [showMacroTrackInNav, setShowMacroTrackInNav] = useState(false);
  const [singleMacroIndex, setSingleMacroIndex] = useState(null);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [firstMacroIndex, setFirstMacroIndex] = useState(null);
  const [trackableSections, setTrackableSections] = useState(0);

  const handleTrackMacros = () => {
    if (!isPlus) {
      setShowPlusModal(true);
      return;
    }
    showMacrosModal(firstMacroIndex);
  };

  const handlePlusModalClose = () => {
    setShowPlusModal(false);
  };

  //Check if recipe has one section with macros only, then show macro track edit in nav
  const checkSingleMacros = () => {
    let macroSections = 0;
    let macroSectionIndex = null;
    for (let i = 0; i < recipe?.sections?.length; i++) {
      if (recipe?.sections[i]?.macros?.length > 0) {
        macroSections++;
        macroSectionIndex = i;
        if (macroSections === 1) {
          setFirstMacroIndex(i);
        }
      }
    }
    if (macroSections === 1) {
      setSingleMacroIndex(macroSectionIndex);
    }
    if (macroSections > 1) {
      setShowMacroTrackInNav(true);
      setTrackableSections(macroSections);
    }
  };

  useEffect(() => {
    checkSingleMacros();
  }, [recipe]);

  const handleResize = (initial = false) => {
    if (navRef.current) {
      const height = Math.floor(navRef.current.getBoundingClientRect().height);
      setNavHeight(height);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(true); // Call it initially to set the height

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    handleResize(false);
  }, [trigger, showMacroTrackInNav]);

  return (
    <Slide in={!trigger} direction="down">
      <div
        className={`fixed top-0 left-0 right-0 bg-background z-50 md:ml-[5rem] flex border-b border-separator ${
          (recipe?.sections && recipe?.sections?.length > 1) || showMacroTrackInNav
            ? "flex-col"
            : "flex-col md:flex-row md:justify-between"
        }`}
        ref={navRef}
      >
        <div className="px-4 md:px-8 pb-4 pt-[calc(env(safe-area-inset-top)+1rem)]">
          <div>
            {!isPlus && (
              <CustomModal open={showPlusModal} handleClose={handlePlusModalClose} type="bottom">
                <PlusModal handleClose={handlePlusModalClose} />
              </CustomModal>
            )}
            {showMacroTrackInNav && (
              <CustomModal open={modals.macrosModal.show} handleClose={hideMacrosModal} type="bottomToCentered">
                <MacrosModal
                  section={recipe?.sections?.[modals.macrosModal.sectionIndex]}
                  sectionIndex={modals.macrosModal.sectionIndex}
                  handleClose={hideMacrosModal}
                />
              </CustomModal>
            )}
          </div>
          <div className="flex flex-row items-end justify-between">
            <div className="flex flex-col flex-grow">
              <Link to={user && recipe && user.username && recipe.url ? `/${user.username}/${recipe.url}` : `/`}>
                <Typography variant="h5" className="font-semibold md:max-w-70%">
                  {recipe && recipe.name}
                </Typography>
              </Link>

              <Typography variant="subtitle1" className="font-normal text-mainGreen">
                {user && "@" + user.username}
              </Typography>
            </div>
            {showMacroTrackInNav && (
              <Button
                className="p-0 hover:bg-transparent flex flex-col items-start justify-end xs:items-end w-fit flex-shrink-0 mt-1"
                disableRipple
                onClick={() => handleTrackMacros()}
              >
                {recipe?.sections?.[singleMacroIndex]?.servingsEaten && isPlus && (
                  <Typography variant="subtitle2" className="font-normal leading-snug">
                    Eating{" "}
                    <span className="font-medium text-mainGreen">
                      {recipe?.sections?.[singleMacroIndex]?.servingsEaten}
                    </span>{" "}
                    serving
                    {recipe?.sections?.[singleMacroIndex]?.servingsEaten !== 1 ? "s" : ""}
                  </Typography>
                )}
                {trackableSections > 1 && (
                  <Typography variant="subtitle2" className="font-normal leading-snug text-xs text-secondaryText-600">
                    {trackableSections} sections
                  </Typography>
                )}

                {singleMacroIndex !== null &&
                recipe?.sections?.[singleMacroIndex]?.macros[0] &&
                recipe?.sections?.[singleMacroIndex]?.macros[0].name === "Calories" ? (
                  <Typography variant="subtitle2" className="font-normal leading-snug text-xs text-secondaryText-600">
                    <span className="font-medium">{recipe?.sections?.[singleMacroIndex]?.macros[0].amount}</span>{" "}
                    kcal/serving
                  </Typography>
                ) : (
                  <div></div>
                )}
                <Button className="p-0 hover:bg-transparent mt-1" disableRipple>
                  <Typography variant="subtitle2" className="text-mainGreen leading-snug text-xs font-medium">
                    Track Macros
                  </Typography>
                </Button>
              </Button>
            )}
          </div>
        </div>

        <div className={`flex justify-between items-end px-4 md:px-8 pb-4 md:pb-8 flex-col md:flex-row`}>
          {recipe?.sections && recipe?.sections?.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ShadcnButton
                  variant="outline"
                  className="w-full md:w-auto md:min-w-[400px] justify-between font-normal rounded-lg border-secondaryText-200 hover:border-primaryText font-sans text-md"
                >
                  {currentSection === "all"
                    ? "All Sections"
                    : recipe && recipe?.sections?.[currentSection]?.title
                    ? recipe.sections[currentSection].title
                    : "No sections"}
                  <ChevronDown className="w-3 opacity-50 ml-2" />
                </ShadcnButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-[8px] z-[1301]">
                <DropdownMenuItem
                  onSelect={() => {
                    const selectedIndex = "all";
                    setWalkthroughSection(selectedIndex);
                  }}
                >
                  <span className={`${currentSection === "all" ? "font-medium text-mainGreen " : ""}`}>
                    All Sections
                  </span>
                </DropdownMenuItem>
                {recipe && recipe.sections ? (
                  recipe.sections.map((section, i) => (
                    <DropdownMenuItem
                      key={section.id}
                      onSelect={() => {
                        const selectedIndex = recipe.sections.findIndex((s) => s.title === section.title);
                        setWalkthroughSection(selectedIndex);
                      }}
                    >
                      <span className={`${currentSection === i ? "font-medium text-mainGreen " : ""}`}>
                        {section.title}
                      </span>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem>No sections</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div></div>
          )}
          <div
            className={`w-full md:w-auto flex flex-col ${
              !(recipe?.sections && recipe?.sections?.length > 1) ? "items-end" : ""
            }`}
          >
            <Button
              variant="contained"
              className="mt-2 md:mt-0 bg-mainGreen text-background w-full md:w-auto"
              disableElevation
              size="small"
              onClick={() => handleFinishRecipe()}
            >
              <Typography variant="subtitle1">Finish Recipe</Typography>
            </Button>
          </div>
        </div>
      </div>
    </Slide>
  );
};

WalkthroughNav.propTypes = {
  walkthrough: PropTypes.object.isRequired,
  setWalkthroughSection: PropTypes.func.isRequired,
  handleFinishRecipe: PropTypes.func.isRequired,
  plus: PropTypes.object.isRequired,
  showMacrosModal: PropTypes.func.isRequired,
  hideMacrosModal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  walkthrough: state.walkthrough,
  plus: state.plus,
});

export default connect(mapStateToProps, { setWalkthroughSection, showMacrosModal, hideMacrosModal })(WalkthroughNav);
